import React, { useRef, useState } from 'react';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Spinner, Alert, InputGroup, Badge } from 'react-bootstrap';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CubeIcon,
  TagIcon,
  MagnifyingGlassIcon,
  FolderPlusIcon,
  XMarkIcon,
  PhotoIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import CropModal from '../common/CropModal';
import { useCompanyProducts, UNIT_OPTIONS } from './hooks/useCompanyProducts';

const CompanyProducts: React.FC = () => {
  // Crop modal state (local to this component)
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [imageCropSrc, setImageCropSrc] = useState<string | null>(null);
  const [imageCropFileName, setImageCropFileName] = useState('');

  const {
    isExpired,
    products,
    categories,
    loading,
    filters,
    setFilters,
    showProductModal,
    editingProduct,
    productForm,
    setProductForm,
    saving,
    feedback,
    pendingImagePreview,
    setPendingImageFile,
    setPendingImagePreview,
    showCategoryModal,
    categoryName,
    categoryDesc,
    savingCategory,
    setCategoryModal,
    isAddingNewCategory,
    newInlineCategoryName,
    setInlineCategory,
    showDeleteModal,
    deletingProduct,
    deleting,
    filtered,
    handleOpenCreate,
    handleOpenEdit,
    handleSaveProduct,
    handleConfirmDelete,
    handleDeleteProduct,
    handleCreateCategory,
    handleDeleteCategory,
    closeProductModal,
    closeCategoryModal,
    closeDeleteModal,
    clearSuccess,
    clearError,
    cancelInlineCategory,
    handleCategorySelectChange,
  } = useCompanyProducts();

  if (loading) {
    return (
      <CompanyLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 text-muted">Loading products...</p>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {feedback.success && (
          <Alert variant="success" dismissible onClose={clearSuccess} className="mb-3">
            {feedback.success}
          </Alert>
        )}
        {feedback.error && !showProductModal && (
          <Alert variant="danger" dismissible onClose={clearError} className="mb-3">
            {feedback.error}
          </Alert>
        )}

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{ 
              backgroundColor: '#ecfdf5', 
              borderRadius: '8px', 
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CubeIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Products</h4>
            <Badge 
              bg="" 
              style={{ 
                backgroundColor: '#ecfdf5', 
                color: '#059669', 
                fontSize: '0.75rem',
                fontWeight: '600',
                padding: '4px 10px',
                borderRadius: '6px'
              }}
            >
              {products.length}
            </Badge>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-success"
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => setCategoryModal(prev => ({ ...prev, show: true }))}

              disabled={isExpired}
              style={{ padding: '6px 14px' }}
            >
              <FolderPlusIcon style={{ width: '15px', height: '15px' }} />
              <span className="d-none d-sm-inline">Manage Categories</span>
            </Button>
            <Button
              variant="success"
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={handleOpenCreate}
              disabled={isExpired}
              style={{ padding: '6px 14px' }}
            >
              <PlusIcon style={{ width: '15px', height: '15px' }} />
              Add Product
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
          <Card.Body className="py-3">
            <Row className="g-2 align-items-center">
              <Col xs={12} md={6}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px 0 0 6px' }}>
                    <MagnifyingGlassIcon style={{ width: '15px', height: '15px', color: '#94a3b8' }} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by name or SKU..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    style={{ border: '1px solid #e2e8f0', borderRadius: '0 6px 6px 0' }}
                  />
                  {filters.search && (
                    <Button variant="outline-secondary" size="sm" onClick={() => setFilters(prev => ({ ...prev, search: '' }))} style={{ borderRadius: '6px' }}>
                      <XMarkIcon style={{ width: '14px', height: '14px' }} />
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col xs={12} md={3}>
                <Form.Select
                  size="sm"
                  value={filters.category}
                  onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                  style={{ border: '1px solid #e2e8f0', borderRadius: '6px' }}
                >
                  <option value="all">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={3} className="text-muted" style={{ fontSize: '0.85rem' }}>
                Showing {filtered.length} of {products.length} products
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Products Table */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>Product</th>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>SKU</th>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }} className="d-none d-md-table-cell">Category</th>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>Price</th>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }} className="d-none d-lg-table-cell">Unit</th>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }} className="d-none d-lg-table-cell">Min Qty</th>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }} className="d-none d-md-table-cell">Stock</th>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>Status</th>
                  <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-muted">
                      <CubeIcon style={{ width: '48px', height: '48px', color: '#d1d5db', marginBottom: '12px' }} />
                      <p className="mb-1 fw-semibold">No products yet</p>
                      <p style={{ fontSize: '0.85rem' }}>Add your first product so your customers can start ordering.</p>
                      {!isExpired && (
                        <Button variant="success" size="sm" onClick={handleOpenCreate}>
                          <PlusIcon style={{ width: '16px', height: '16px' }} /> Add Product
                        </Button>
                      )}
                    </td>
                  </tr>
                ) : (
                  filtered.map((product) => (
                    <tr key={product.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div className="d-flex align-items-center gap-2">
                          {(product as any).image
                            ? <img src={(product as any).image} alt={product.name} style={{ width: 40, height: 40, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                            : <div style={{ width: 40, height: 40, borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: '1.2rem' }}>🍞</span>
                              </div>
                          }
                          <div>
                            <div className="fw-semibold" style={{ fontSize: '0.9rem', color: '#1e293b' }}>{product.name}</div>
                            {product.description && (
                              <div style={{ fontSize: '0.78rem', color: '#94a3b8', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {product.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: '0.85rem', fontFamily: 'monospace', color: '#64748b', padding: '12px 16px' }}>{product.sku}</td>
                      <td className="d-none d-md-table-cell" style={{ padding: '12px 16px' }}>
                        <span style={{ 
                          fontSize: '0.78rem', 
                          backgroundColor: '#f0fdf4', 
                          color: '#065f46', 
                          padding: '3px 10px', 
                          borderRadius: '4px',
                          fontWeight: '500',
                          border: '1px solid #bbf7d0'
                        }}>
                          {product.category.name}
                        </span>
                      </td>
                      <td className="fw-semibold" style={{ fontSize: '0.9rem', color: '#1e293b', padding: '12px 16px' }}>
                        ${Number(product.price_per_unit).toFixed(2)}
                      </td>
                      <td className="d-none d-lg-table-cell" style={{ fontSize: '0.85rem', color: '#64748b', padding: '12px 16px' }}>{product.unit}</td>
                      <td className="d-none d-lg-table-cell" style={{ fontSize: '0.85rem', color: '#64748b', padding: '12px 16px' }}>{product.minimum_order_quantity}</td>
                      <td className="d-none d-md-table-cell" style={{ fontSize: '0.85rem', color: '#64748b', padding: '12px 16px' }}>{product.stock_quantity}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ 
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          padding: '3px 10px',
                          borderRadius: '4px',
                          backgroundColor: product.is_available ? '#ecfdf5' : '#f1f5f9',
                          color: product.is_available ? '#059669' : '#64748b'
                        }}>
                          {product.is_available ? 'Active' : 'Inactive'}
                        </span>
                        {(product as any).retail_published && (
                          <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', backgroundColor: '#eff6ff', color: '#1e40af', marginLeft: '4px' }}>
                            🌐 Retail
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleOpenEdit(product)}
                            disabled={isExpired}
                            title="Edit product"
                            style={{ padding: '4px 8px', lineHeight: 1 }}
                          >
                            <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleConfirmDelete(product)}
                            disabled={isExpired}
                            title="Delete product"
                            style={{ padding: '4px 8px', lineHeight: 1 }}
                          >
                            <TrashIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </Container>

      {/* ── Product Modal ── */}
      <Modal show={showProductModal} onHide={closeProductModal} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#f0fdf4', borderBottom: '2px solid #065f46' }}>
          <Modal.Title style={{ color: '#065f46', fontSize: '1.1rem' }}>
            <CubeIcon style={{ width: '20px', height: '20px', marginRight: '8px' }} />
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {feedback.error && <Alert variant="danger" className="py-2" style={{ fontSize: '0.85rem' }}>{feedback.error}</Alert>}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Product Name *</Form.Label>
                <Form.Control
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  placeholder="e.g. Sourdough Loaf"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>SKU *</Form.Label>
                <Form.Control
                  value={productForm.sku}
                  onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                  placeholder="e.g. SD-LOAF-001"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Category *</Form.Label>
                {isAddingNewCategory ? (
                  <InputGroup>
                    <Form.Control
                      value={newInlineCategoryName}
                      onChange={(e) => setInlineCategory(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="New category name..."
                      aria-label="New category name"
                    />
                    <Button
                      variant="outline-secondary"
                      title="Cancel"
                      onClick={cancelInlineCategory}
                    >
                      <XMarkIcon style={{ width: '14px', height: '14px' }} />
                    </Button>
                  </InputGroup>
                ) : (
                  <Form.Select
                    value={productForm.category_id}
                    style={{ color: productForm.category_id ? '#212529' : '#6c757d', borderRadius: '0.375rem', padding: '0.375rem 0.75rem', fontSize: '0.875rem', height: 'calc(1.5em + 0.75rem + 2px)' }}
                    onChange={(e) => handleCategorySelectChange(e.target.value)}
                  >
                    <option value="">Select category...</option>
                    {categories.map((c) => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                    <option value="__new__" style={{ fontWeight: 600, color: '#065f46' }}>＋ Add New Category...</option>
                  </Form.Select>
                )}
                {!isAddingNewCategory && categories.length === 0 && (
                  <Form.Text className="text-muted" style={{ fontSize: '0.78rem' }}>
                    No categories yet — select "＋ Add New Category" above.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Unit</Form.Label>
                <Form.Select
                  value={productForm.unit}
                  style={{ color: '#212529', borderRadius: '0.375rem', padding: '0.375rem 0.75rem', fontSize: '0.875rem', height: 'calc(1.5em + 0.75rem + 2px)' }}
                  onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                >
                  {UNIT_OPTIONS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Price per Unit ($) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={productForm.price_per_unit}
                  onChange={(e) => setProductForm({ ...productForm, price_per_unit: e.target.value })}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Min Order Qty</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={productForm.minimum_order_quantity}
                  onChange={(e) => setProductForm({ ...productForm, minimum_order_quantity: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Stock Quantity</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={productForm.stock_quantity}
                  onChange={(e) => setProductForm({ ...productForm, stock_quantity: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Product Image</Form.Label>
                <div className="d-flex align-items-center gap-3">
                  {pendingImagePreview
                    ? <img src={pendingImagePreview} alt="product" style={{ width: 72, height: 72, borderRadius: '10px', objectFit: 'cover', border: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => imageInputRef.current?.click()} />
                    : <div style={{ width: 72, height: 72, borderRadius: '10px', background: '#f8fafc', border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => imageInputRef.current?.click()}>
                        <PhotoIcon style={{ width: 24, height: 24, color: '#cbd5e1' }} />
                      </div>
                  }
                  <div>
                    <Button size="sm" variant="outline-secondary" style={{ borderRadius: '8px', fontSize: '0.8rem' }} onClick={() => imageInputRef.current?.click()}>
                      {pendingImagePreview ? 'Change & Crop' : 'Upload & Crop'}
                    </Button>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (imageInputRef.current) imageInputRef.current.value = '';
                        setImageCropSrc(URL.createObjectURL(file));
                        setImageCropFileName(file.name);
                      }}
                    />
                    <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '4px' }}>Square (1:1) · Shown on store page</div>
                    {pendingImagePreview && (
                      <button style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.72rem', cursor: 'pointer', padding: 0, marginTop: 2 }}
                        onClick={() => { setPendingImageFile(null); setPendingImagePreview(null); }}>
                        Remove image
                      </button>
                    )}
                  </div>
                </div>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  placeholder="Optional product description..."
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Allergens</Form.Label>
                <Form.Control
                  size="sm"
                  value={(productForm as any).allergens || ''}
                  onChange={(e) => setProductForm({ ...productForm, allergens: e.target.value } as any)}
                  placeholder="e.g. Gluten, Eggs, Dairy, Nuts"
                />
                <Form.Text muted>Separate allergens with commas</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold" style={{ fontSize: '0.85rem' }}>Ingredients</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={(productForm as any).ingredients || ''}
                  onChange={(e) => setProductForm({ ...productForm, ingredients: e.target.value } as any)}
                  placeholder="Full ingredients list..."
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Check
                type="switch"
                id="is_available"
                label="Available for wholesale ordering"
                checked={productForm.is_available}
                onChange={(e) => setProductForm({ ...productForm, is_available: e.target.checked })}
              />
            </Col>
            <Col xs={12}>
              <Form.Check
                type="switch"
                id="retail_published"
                label={<>Publish to retail store <span style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>(/store page)</span></>}
                checked={(productForm as any).retail_published || false}
                onChange={(e) => setProductForm({ ...productForm, retail_published: e.target.checked } as any)}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={closeProductModal}>Cancel</Button>
          <Button variant="success" onClick={handleSaveProduct} disabled={saving}>
            {saving ? <><Spinner animation="border" size="sm" className="me-1" /> Saving...</> : editingProduct ? 'Update Product' : 'Create Product'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Category Modal ── */}
      <Modal show={showCategoryModal} onHide={closeCategoryModal} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#f0fdf4', borderBottom: '2px solid #065f46' }}>
          <Modal.Title style={{ color: '#065f46', fontSize: '1.1rem' }}>
            <TagIcon style={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Manage Categories
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Existing categories */}
          {categories.length > 0 && (
            <div className="mb-3">
              <p className="fw-semibold mb-2" style={{ fontSize: '0.85rem', color: '#065f46' }}>Existing Categories</p>
              <div className="d-flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Badge
                    key={c.id}
                    bg="light"
                    text="dark"
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: '0.82rem', padding: '6px 12px', border: '1px solid #e5e7eb' }}
                  >
                    {c.name}
                    <XMarkIcon
                      style={{ width: '14px', height: '14px', cursor: 'pointer', color: '#dc2626' }}
                      onClick={() => handleDeleteCategory(c.id)}
                    />
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <hr />
          <p className="fw-semibold mb-2" style={{ fontSize: '0.85rem', color: '#065f46' }}>Add New Category</p>
          <Form.Group className="mb-2">
            <Form.Control
              size="sm"
              value={categoryName}
              onChange={(e) => setCategoryModal(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Category name, e.g. Breads"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              size="sm"
              value={categoryDesc}
              onChange={(e) => setCategoryModal(prev => ({ ...prev, desc: e.target.value }))}
              placeholder="Optional description"
            />
          </Form.Group>
          <Button
            variant="success"
            size="sm"
            onClick={handleCreateCategory}
            disabled={savingCategory || !categoryName.trim()}
          >
            {savingCategory ? <Spinner animation="border" size="sm" /> : <><FolderPlusIcon style={{ width: '16px', height: '16px' }} /> Add Category</>}
          </Button>
        </Modal.Body>
      </Modal>

      {/* ── Delete Confirmation ── */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Delete Product</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.9rem' }}>
          Are you sure you want to delete <strong>{deletingProduct?.name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={closeDeleteModal}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDeleteProduct} disabled={deleting}>
            {deleting ? <Spinner animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Crop Modal */}
      {imageCropSrc && (
        <CropModal
          imageSrc={imageCropSrc}
          fileName={imageCropFileName}
          aspect={1}
          cropShape="rect"
          title="Crop Product Image"
          onDone={(file, previewUrl) => {
            setPendingImageFile(file);
            setPendingImagePreview(previewUrl);
            setImageCropSrc(null);
          }}
          onClose={() => setImageCropSrc(null)}
        />
      )}
    </CompanyLayout>
  );
};

export default CompanyProducts;
